import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Loader2,
  Check,
  Zap,
  Users,
  ChevronRight,
  User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SCORE_UNITS = ['wcpm', '%', 'points', 'rating', 'frequency', 'problems correct', 'words', 'other'];

export default function QuickEntryPage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [entries, setEntries] = useState({});
  
  // Default values that apply to all entries
  const [defaultDate, setDefaultDate] = useState(new Date().toISOString().split('T')[0]);
  const [defaultMeasure, setDefaultMeasure] = useState('');
  const [defaultUnit, setDefaultUnit] = useState('wcpm');
  // Note: defaultEnteredBy removed - backend sets from authenticated user
  
  // Track saved count for success message
  const [savedCount, setSavedCount] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Refs for auto-focus on next row
  const scoreRefs = useRef({});

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await apiService.getStudentsForQuickEntry();
      setStudents(response.data);
      
      // Initialize entries for each student
      const initialEntries = {};
      response.data.forEach(student => {
        initialEntries[student.id] = {
          score: '',
          measure_name: student.last_measure || student.baseline_measure_name || '',
          score_unit: student.last_score_unit || 'wcpm',
          note: '',
          saved: false
        };
      });
      setEntries(initialEntries);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  // Apply defaults to all unsaved entries
  const applyDefaultMeasure = () => {
    if (!defaultMeasure) return;
    setEntries(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(studentId => {
        if (!updated[studentId].saved) {
          updated[studentId] = {
            ...updated[studentId],
            measure_name: defaultMeasure
          };
        }
      });
      return updated;
    });
    toast.success('Default measure applied to all rows');
  };

  const applyDefaultUnit = () => {
    setEntries(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(studentId => {
        if (!updated[studentId].saved) {
          updated[studentId] = {
            ...updated[studentId],
            score_unit: defaultUnit
          };
        }
      });
      return updated;
    });
    toast.success('Default unit applied to all rows');
  };

  const handleEntryChange = (studentId, field, value) => {
    setEntries(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value,
        saved: false
      }
    }));
  };

  const handleSaveAndNext = async (studentId, currentIndex) => {
    const entry = entries[studentId];
    if (!entry.score || !entry.measure_name) {
      toast.error('Please enter score and measure name');
      return;
    }

    setSaving(prev => ({ ...prev, [studentId]: true }));
    
    try {
      // Note: entered_by not sent - backend sets from authenticated user
      await apiService.createProgress({
        student_id: studentId,
        date_collected: defaultDate,
        measure_name: entry.measure_name,
        score: parseFloat(entry.score),
        score_unit: entry.score_unit,
        note: entry.note
      });
      
      setEntries(prev => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          saved: true
        }
      }));
      
      setSavedCount(prev => prev + 1);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      // Focus next row's score input
      const nextIndex = currentIndex + 1;
      if (nextIndex < students.length) {
        const nextStudentId = students[nextIndex].id;
        if (scoreRefs.current[nextStudentId]) {
          scoreRefs.current[nextStudentId].focus();
        }
      }
      
      toast.success('Saved!', { duration: 1500 });
    } catch (error) {
      console.error('Failed to save progress:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [studentId]: false }));
    }
  };

  const handleKeyPress = (e, studentId, currentIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveAndNext(studentId, currentIndex);
    }
  };

  const handleSaveAll = async () => {
    const entriesToSave = Object.entries(entries)
      .filter(([_, entry]) => entry.score && entry.measure_name && !entry.saved)
      .map(([studentId, entry]) => ({
        student_id: studentId,
        date_collected: defaultDate,
        measure_name: entry.measure_name,
        score: parseFloat(entry.score),
        score_unit: entry.score_unit,
        note: entry.note
        // Note: entered_by not sent - backend sets from authenticated user
      }));

    if (entriesToSave.length === 0) {
      toast.info('No new entries to save');
      return;
    }

    setSaving(prev => ({ ...prev, all: true }));
    
    try {
      const response = await apiService.createBulkProgress(entriesToSave);
      
      // Mark all saved entries
      const savedStudentIds = response.data.entries.map(e => e.student_id);
      setEntries(prev => {
        const updated = { ...prev };
        savedStudentIds.forEach(id => {
          if (updated[id]) {
            updated[id].saved = true;
          }
        });
        return updated;
      });
      
      setSavedCount(prev => prev + response.data.created);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      
      toast.success(`Saved ${response.data.created} progress entries!`);
      
      // Refresh to get updated scores
      fetchStudents();
    } catch (error) {
      console.error('Failed to save progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(prev => ({ ...prev, all: false }));
    }
  };

  const getUnsavedCount = () => {
    return Object.values(entries).filter(e => e.score && e.measure_name && !e.saved).length;
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'Tier 1': return 'badge-tier1';
      case 'Tier 2': return 'badge-tier2';
      case 'Tier 3': return 'badge-tier3';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="quick-entry-page">
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-[#595959] hover:text-[#1C1C1C] text-sm font-medium">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>
      </div>

      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Zap className="h-7 w-7 text-[#2C4C3B]" />
            Quick Progress Entry
          </h1>
          <p className="page-subtitle">Enter progress data for multiple students in one session</p>
        </div>
        <div className="flex items-center gap-3">
          {showSuccessMessage && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium animate-pulse">
              <Check className="h-4 w-4" />
              {savedCount} entries saved
            </div>
          )}
          <Button
            onClick={handleSaveAll}
            disabled={saving.all || getUnsavedCount() === 0}
            className="bg-[#2C4C3B] hover:bg-[#1E3629]"
            data-testid="save-all-btn"
          >
            {saving.all ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save All ({getUnsavedCount()})
          </Button>
        </div>
      </div>

      {/* Default Settings Panel */}
      <div className="card mb-6">
        <div className="card-content py-4">
          <div className="text-sm font-medium text-[#1C1C1C] mb-3">Default Values (apply to all rows)</div>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-[160px]">
              <Label className="text-xs">Date Collected</Label>
              <Input
                type="date"
                value={defaultDate}
                onChange={(e) => setDefaultDate(e.target.value)}
                className="h-9"
                data-testid="default-date-input"
              />
            </div>
            
            <div className="flex items-end gap-2">
              <div className="w-[160px]">
                <Label className="text-xs">Default Measure</Label>
                <Input
                  type="text"
                  value={defaultMeasure}
                  onChange={(e) => setDefaultMeasure(e.target.value)}
                  placeholder="e.g., DIBELS ORF"
                  className="h-9"
                  data-testid="default-measure-input"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={applyDefaultMeasure}
                disabled={!defaultMeasure}
                className="h-9"
              >
                Apply
              </Button>
            </div>
            
            <div className="flex items-end gap-2">
              <div className="w-[120px]">
                <Label className="text-xs">Default Unit</Label>
                <Select value={defaultUnit} onValueChange={setDefaultUnit}>
                  <SelectTrigger className="h-9" data-testid="default-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCORE_UNITS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={applyDefaultUnit}
                className="h-9"
              >
                Apply
              </Button>
            </div>
            
            <div className="w-[200px]">
              <Label className="text-xs flex items-center gap-1">
                <User className="h-3 w-3" />
                Entered By
              </Label>
              <div className="px-3 py-1.5 bg-[#F3F4F1] border border-[#E5E5E5] rounded-lg h-9 flex items-center" data-testid="entered-by-display">
                <span className="text-sm text-[#1C1C1C] truncate">{user?.name || user?.email || 'Unknown'}</span>
              </div>
            </div>
            
            <div className="text-sm text-[#595959] flex items-center gap-2 ml-auto">
              <Users className="h-4 w-4" />
              {students.length} students
            </div>
          </div>
          <p className="text-xs text-[#595959] mt-2">
            Tip: Press Enter after entering a score to save and move to the next row. All entries are attributed to your account.
          </p>
        </div>
      </div>

      {/* Students Table */}
      {students.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">
              <Users size={32} />
            </div>
            <h3 className="empty-title">No active students</h3>
            <p className="empty-text">Add students to start tracking progress</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="quick-entry-table">
              <thead>
                <tr>
                  <th style={{ width: '180px' }}>Student</th>
                  <th style={{ width: '60px' }}>Gr</th>
                  <th style={{ width: '60px' }}>Tier</th>
                  <th style={{ width: '90px' }}>Last</th>
                  <th style={{ width: '130px' }}>Measure</th>
                  <th style={{ width: '90px' }}>Score</th>
                  <th style={{ width: '100px' }}>Unit</th>
                  <th style={{ width: '120px' }}>Note</th>
                  <th style={{ width: '90px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const entry = entries[student.id] || {};
                  return (
                    <tr 
                      key={student.id} 
                      data-testid={`quick-entry-row-${student.id}`}
                      className={entry.saved ? 'bg-green-50' : ''}
                    >
                      <td>
                        <Link to={`/students/${student.id}`} className="font-medium text-[#2C4C3B] hover:underline text-sm">
                          {student.full_name}
                        </Link>
                      </td>
                      <td className="text-sm">{student.grade}</td>
                      <td>
                        <span className={`badge text-xs ${getTierBadgeClass(student.current_tier)}`}>
                          {student.current_tier?.replace('Tier ', 'T')}
                        </span>
                      </td>
                      <td className="text-[#595959] text-sm">
                        {student.last_score !== null ? (
                          <span title={student.last_date}>
                            {student.last_score}
                          </span>
                        ) : (
                          <span className="text-[#A3A3A3]">—</span>
                        )}
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={entry.measure_name || ''}
                          onChange={(e) => handleEntryChange(student.id, 'measure_name', e.target.value)}
                          placeholder="Measure"
                          className="h-8 text-sm"
                          disabled={entry.saved}
                          data-testid={`measure-${student.id}`}
                        />
                      </td>
                      <td>
                        <Input
                          ref={el => scoreRefs.current[student.id] = el}
                          type="number"
                          step="0.1"
                          value={entry.score || ''}
                          onChange={(e) => handleEntryChange(student.id, 'score', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, student.id, index)}
                          placeholder="Score"
                          className="h-8 text-sm"
                          disabled={entry.saved}
                          data-testid={`score-${student.id}`}
                        />
                      </td>
                      <td>
                        <Select 
                          value={entry.score_unit || 'wcpm'} 
                          onValueChange={(v) => handleEntryChange(student.id, 'score_unit', v)}
                          disabled={entry.saved}
                        >
                          <SelectTrigger className="h-8 text-sm" data-testid={`unit-${student.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SCORE_UNITS.map((unit) => (
                              <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <Input
                          type="text"
                          value={entry.note || ''}
                          onChange={(e) => handleEntryChange(student.id, 'note', e.target.value)}
                          placeholder="Note"
                          className="h-8 text-sm"
                          disabled={entry.saved}
                          data-testid={`note-${student.id}`}
                        />
                      </td>
                      <td>
                        {entry.saved ? (
                          <span className="inline-flex items-center text-green-600 text-sm font-medium">
                            <Check className="h-4 w-4 mr-1" />
                            Saved
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSaveAndNext(student.id, index)}
                            disabled={saving[student.id] || !entry.score || !entry.measure_name}
                            className="h-8"
                            title="Save & Next (Enter)"
                            data-testid={`save-${student.id}`}
                          >
                            {saving[student.id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-1" />
                                <ChevronRight className="h-3 w-3" />
                              </>
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
